<template>
  <div class="relative flex flex-col min-w-0 break-words w-full px-4 py-8">
    <div class="rounded-t mb-0 pb-3 border-0">
      <div class="flex flex-wrap items-center">
        <div class="relative w-full max-w-full flex-grow flex-1">
          <h3 class="text-3xl text-blueGray-700 font-headline mt-4 mx-5">
            Expert Recommendations for Space
          </h3>
        </div>
      </div>
    </div>

    <!-- Create new recommendation form -->
    <div class="bg-white p-6 shadow rounded mb-8">
      <h4 class="text-xl font-bold mb-4">Create New Recommendation</h4>
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2"
          >Context</label
        >
        <textarea
          v-model="newRec.context"
          class="w-full border p-2"
          rows="3"
          placeholder="Company and department paragraph"
        ></textarea>
      </div>
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2">Theme</label>
        <textarea
          v-model="newRec.theme"
          class="w-full border p-2"
          rows="2"
          placeholder="Describe the work that members are hoping to accomplish"
        ></textarea>
      </div>
      <button
        class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        @click="createRecommendation"
      >
        Generate Recommendations
      </button>
    </div>

    <!-- List recommendations -->
    <div
      v-for="rec in recommendations"
      :key="rec.hashedId"
      class="bg-white p-6 shadow rounded mb-8"
    >
      <div class="flex justify-between items-center mb-4">
        <h4 class="text-xl font-bold">Recommendation: {{ rec.subject }}</h4>
        <span v-if="rec.sentAt" class="text-green-600 font-bold"
          >Sent at {{ new Date(rec.sentAt).toLocaleString() }}</span
        >
        <span v-else class="text-yellow-600 font-bold">Draft</span>
      </div>
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2"
          >Subject</label
        >
        <input
          v-model="rec.subject"
          class="w-full border p-2"
          :disabled="!!rec.sentAt"
          @change="updateRecommendation(rec)"
        />
      </div>

      <div class="mb-4 bg-gray-100 p-3 rounded">
        <label class="block text-gray-700 text-sm font-bold mb-1"
          >Elasticsearch Terms (Generated by AI)</label
        >
        <pre class="text-xs overflow-auto">{{
          JSON.stringify(rec.elasticsearchTerms, null, 2)
        }}</pre>
      </div>

      <h5 class="font-bold mt-4 mb-2">Recommended Experts (Results)</h5>
      <div
        v-if="!rec.results || rec.results.length === 0"
        class="text-gray-500 italic mb-4"
      >
        No experts were returned for these search terms.
      </div>
      <div
        v-for="res in rec.results"
        :key="res.hashedId"
        class="border p-4 mb-2 rounded flex flex-col gap-2"
      >
        <div class="flex justify-between">
          <span class="font-bold">{{ res.profile.displayName }}</span>
          <label v-if="!rec.sentAt">
            <input
              v-model="res.isIncluded"
              type="checkbox"
              @change="updateResult(res)"
            />
            Included
          </label>
          <span v-else class="text-sm text-gray-600">
            {{ res.isIncluded ? "Included" : "Not Included" }}
          </span>
        </div>
        <textarea
          v-model="res.reasoning"
          class="w-full border p-2"
          rows="2"
          :disabled="!!rec.sentAt"
          @change="updateResult(res)"
        ></textarea>
        <div v-if="!rec.sentAt">
          Sort Order:
          <input
            v-model.number="res.sortOrder"
            type="number"
            class="border p-1 w-20"
            @change="updateResult(res)"
          />
        </div>
      </div>

      <div class="mt-4 flex gap-4">
        <button
          class="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          @click="compileEmail(rec)"
        >
          Preview Email
        </button>
        <button
          v-if="!rec.sentAt"
          class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          @click="sendEmail(rec)"
        >
          Send Email to Space
        </button>
        <button
          v-if="!rec.sentAt"
          class="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded ml-auto"
          @click="deleteRecommendation(rec)"
        >
          Delete Draft
        </button>
      </div>

      <div v-if="rec.previewHtml" class="mt-4 border p-4 bg-gray-50">
        <h5 class="font-bold mb-2">Preview:</h5>
        <div v-html="rec.previewHtml"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { utils } from "common-frontend";

const { keysToCamel } = utils;

export default {
  data() {
    return {
      recommendations: [],
      newRec: {
        context: "",
        theme: "",
      },
    };
  },
  computed: {
    spaceId() {
      return this.$route.params.hashedId;
    },
  },
  mounted() {
    this.fetchRecommendations();
  },
  methods: {
    fetchRecommendations() {
      this.$api.axios
        .get(`/collaboration-space/${this.spaceId}/expert-recommendation/`)
        .then((res) => {
          this.recommendations = keysToCamel(res.data);
          // ensure results are sorted
          this.recommendations.forEach((r) => {
            if (r.results) {
              r.results.sort((a, b) => a.sortOrder - b.sortOrder);
            }
          });
        });
    },
    createRecommendation() {
      this.$api.axios
        .post(
          `/collaboration-space/${this.spaceId}/expert-recommendation/`,
          this.newRec,
        )
        .then(() => {
          this.newRec = { context: "", theme: "" };
          this.fetchRecommendations();
        });
    },
    updateRecommendation(rec) {
      this.$api.axios.put(
        `/collaboration-space/${this.spaceId}/expert-recommendation/${rec.hashedId}`,
        {
          subject: rec.subject,
          context: rec.context,
          theme: rec.theme,
        },
      );
    },
    updateResult(res) {
      this.$api.axios.put(
        `/collaboration-space/${this.spaceId}/expert-recommendation-result/${res.hashedId}`,
        {
          isIncluded: res.isIncluded,
          sortOrder: res.sortOrder,
          reasoning: res.reasoning,
        },
      );
    },
    compileEmail(rec) {
      this.$api.axios
        .post(
          `/collaboration-space/${this.spaceId}/expert-recommendation/${rec.hashedId}/compile/`,
        )
        .then((res) => {
          rec.previewHtml = res.data.html;
        });
    },
    sendEmail(rec) {
      if (
        confirm(
          "Are you sure you want to send this email to all space members?",
        )
      ) {
        this.$api.axios
          .post(
            `/collaboration-space/${this.spaceId}/expert-recommendation/${rec.hashedId}/send/`,
          )
          .then(() => {
            this.fetchRecommendations();
          });
      }
    },
    deleteRecommendation(rec) {
      if (
        confirm("Are you sure you want to delete this draft recommendation?")
      ) {
        this.$api.axios
          .delete(
            `/collaboration-space/${this.spaceId}/expert-recommendation/${rec.hashedId}`,
          )
          .then(() => {
            this.fetchRecommendations();
          });
      }
    },
  },
};
</script>
